import type { HandlerFunction } from "../types";
import { useTaskStoreV2 } from "~/stores/task";
import { useObservationStore } from "~/stores/observation";
import { useResidentStore } from "~/stores/resident";
import { useCommunicationStore } from "~/stores/communication";

/**
 * Family Group message handler
 *
 * Handles family group-specific SSE events including:
 * - Safeguarding alerts
 * - Activities and observations
 * - Resident updates
 * - Task management (create, update, delete)
 * - Alert communications
 */
export const handleFamilyGroupMessage: HandlerFunction = (data) => {
  // console.log('👨‍👩‍👧‍👦 Family Group message received:', data)

  const toast = useToast();
  const taskStore = useTaskStoreV2();
  const obsStore = useObservationStore();
  const residentStore = useResidentStore();
  const communicationStore = useCommunicationStore();

  // console.log("[SSE] [handleFamilyGroupMessage]:", data);

  switch (data.type) {
    case "safeguarding_alert": {
      obsStore.needRefresh = true;
      break;
    }

    case "activity_created":
      // New activity created
      break;

    case "observation_added":
      // New observation added
      break;

    case "resident_updated":
      // Resident updated
      residentStore.needRefresh = true;
      break;

    case "resident_added":
      // Resident added
      residentStore.needRefresh = true;
      break;

    case "task.created":
      taskStore.addTaskToSection(data.task);
      break;

    case "task.updated":
      // console.log('Task updated:', data.task)
      taskStore.updateTaskLocal(data.task.id, data.task);
      break;

    case "task.deleted":
      taskStore.removeTaskLocal(data.task.id);
      break;

    case "task.archived":
      // Mark the task as archived in the store and remove it from the section
      // list so it disappears from active task views. The task stays in
      // tasksById so deep-linked modals can still render it read-only.
      taskStore.updateTaskLocal(data.task.id, {
        archived: true,
        archived_at: data.task.archived_at,
      });
      taskStore.removeTaskFromSection(data.task.id, data.task.section_id ?? null);
      break;

    case "task.unarchived":
      // Clear the archived flag and re-add the task to its section list so it
      // reappears in active task views. addTaskToSection also refreshes the
      // task data from the broadcast payload (full serialized task).
      taskStore.updateTaskLocal(data.task.id, { archived: false, archived_at: null });
      taskStore.addTaskToSection(data.task);
      break;

    case "alert.create": {
      // Handle incoming alert from notification service
      // Add to local communication store
      if (data.data) {
        communicationStore.addCommunicationLocal(data.data);
      }

      // Show toast notification for group alert
      toast.add({
        title: data.message || "New Alert",
        description: data.data?.body || "",
        color: "warning",
        icon: "i-lucide-bell",
        duration: 7000,
      });
      break;
    }

    default:
      console.log("Unknown family group message type:", data.type);
  }
};
